/**
 * My Approvals View
 *
 * Displays requests pending the current user's approval with filtering,
 * sorting, and pagination.
 */

import { createRequestList } from '../components/request-list';
import { createSidebarLayout } from '../components/sidebar-layout';
import { apiClient } from '../services/api-client';
import { showToast } from '../components/toast';
import { exportRequestsToCSV } from '../utils/csv-export';
import type { ExpenditureRequest } from '../components/request-list';

/**
 * Create the My Approvals view
 */
export function createMyApprovalsView(): HTMLElement {
  // Create main content
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Page header
  const pageHeader = document.createElement('div');
  pageHeader.className = 'mb-6';
  pageHeader.innerHTML = `
    <p class="text-muted-foreground">Requests pending your approval</p>
  `;

  // Request list container
  const requestListContainer = document.createElement('div');
  requestListContainer.id = 'request-list-container';

  content.appendChild(pageHeader);
  content.appendChild(requestListContainer);

  // Initialize request list after DOM is ready
  setTimeout(() => {
    let allRequests: ExpenditureRequest[] = [];

    createRequestList({
      parent: requestListContainer,
      title: 'Pending Approvals',
      subtitle: 'Click on a request to review and take action',
      endpoint: '/requests/pending-approvals',
      showFilters: false,
      showSearch: false,
      showStatusColumn: false,
      showCreatorColumn: true,
      emptyMessage: 'No requests pending your approval.',
      isPendingApprovals: true,
      enableBulkSelection: true,
      showBulkActions: true,
      onBulkApprove: async (ids: string[]) => {
        try {
          const response = await apiClient.post('/requests/batch-approve', {
            requestIds: ids,
            comment: 'Approved via bulk action',
          });

          const { summary } = (response.data as { data: { summary: { successful: number; failed: number } } }).data;

          if (summary.successful > 0) {
            showToast({
              type: 'success',
              message: `Successfully approved ${summary.successful} request${summary.successful > 1 ? 's' : ''}`,
            });
          }

          if (summary.failed > 0) {
            showToast({
              type: 'error',
              message: `Failed to approve ${summary.failed} request${summary.failed > 1 ? 's' : ''}`,
            });
          }

          // Reload the list to reflect changes
          window.location.reload();
        } catch (error) {
          console.error('Batch approve failed:', error);
          showToast({
            type: 'error',
            message: 'Failed to approve selected requests',
          });
        }
      },
      onBulkReject: async (ids: string[]) => {
        // Show confirmation dialog
        const reason = prompt('Please provide a reason for rejection:');
        if (!reason) return;

        try {
          const response = await apiClient.post('/requests/batch-reject', {
            requestIds: ids,
            reason,
          });

          const { summary } = (response.data as { data: { summary: { successful: number; failed: number } } }).data;

          if (summary.successful > 0) {
            showToast({
              type: 'success',
              message: `Successfully rejected ${summary.successful} request${summary.successful > 1 ? 's' : ''}`,
            });
          }

          if (summary.failed > 0) {
            showToast({
              type: 'error',
              message: `Failed to reject ${summary.failed} request${summary.failed > 1 ? 's' : ''}`,
            });
          }

          // Reload the list to reflect changes
          window.location.reload();
        } catch (error) {
          console.error('Batch reject failed:', error);
          showToast({
            type: 'error',
            message: 'Failed to reject selected requests',
          });
        }
      },
      onBulkExport: (ids: string[]) => {
        // Filter requests by selected IDs
        const selectedRequests = allRequests.filter(r => ids.includes(r.id));
        exportRequestsToCSV(selectedRequests);
        showToast({
          type: 'success',
          message: `Exported ${selectedRequests.length} request${selectedRequests.length > 1 ? 's' : ''} to CSV`,
        });
      },
      viewId: 'my-approvals',
      showColumnPicker: true,
    });

    // Store all requests for export functionality
    // We'll need to access them from the request list
    const requestListEl = requestListContainer.querySelector('.request-list') as HTMLElement & {
      loadRequests?: (...args: unknown[]) => Promise<unknown>;
      requests?: unknown[];
    } | null;
    const originalLoadRequests = requestListEl?.loadRequests;
    if (originalLoadRequests && requestListEl) {
      requestListEl.loadRequests = async function(this: typeof requestListEl, ...args: unknown[]) {
        const result = await originalLoadRequests.apply(this, args);
        allRequests = (this?.requests as ExpenditureRequest[]) || [];
        return result;
      };
    }
  }, 0);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/approvals',
    title: 'My Approvals',
  });
}

/**
 * My Approvals view function for router
 */
export function myApprovalsView(): HTMLElement {
  return createMyApprovalsView();
}
